import { Link } from "react-router-dom";
import { ArrowRight, Cpu, FileCheck2, Handshake, ScanSearch } from "lucide-react";
import GroupHeader from "@/components/GroupHeader";
import GroupFooter from "@/components/GroupFooter";
import SEO from "@/components/SEO";
import GetThePlotFeed from "@/components/GetThePlotFeed";
import WalkTheLand from "@/components/site/WalkTheLand";
import ClosingCTA from "@/components/site/ClosingCTA";
import { ArrowLink, Eyebrow, OutLink, PillButton, Reveal, StatusBadge } from "@/components/site/Bits";
import { useProjects, useSettings } from "@/lib/site";
import { ACTIVITIES } from "@/lib/content";

function Hero() {
    const s = useSettings();
    const [l1, ...rest] = (s.hero_headline || "").split("\n");
    return (
        <section data-testid="home-hero" className="relative isolate min-h-[100svh] flex items-end overflow-hidden bg-forest pb-16 md:pb-24 pt-32">
            <img src="/v2050-hero_coast.jpg" alt="Wild coastal land near Sintra at golden hour, descending toward the Atlantic"
                 className="absolute inset-0 -z-10 h-full w-full object-cover hero-zoom" loading="eager" fetchpriority="high" />
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-forest/50 via-forest/20 to-forest/95" />
            <div className="max-w-7xl mx-auto px-6 md:px-12 w-full text-cream">
                <Reveal><Eyebrow dark>Land Investment &amp; Development · Portugal</Eyebrow></Reveal>
                <Reveal delay={120}>
                    <h1 data-testid="hero-headline" className="font-serif text-5xl sm:text-6xl lg:text-8xl leading-[0.98] tracking-tight mt-8 max-w-5xl">
                        {l1}{rest.length > 0 && <><br /><em className="italic text-sage">{rest.join(" ")}</em></>}
                    </h1>
                </Reveal>
                <div className="mt-10 grid md:grid-cols-12 gap-8 items-end">
                    <Reveal delay={240} className="md:col-span-6">
                        <p data-testid="hero-subheading" className="text-base md:text-lg text-cream/85 leading-relaxed max-w-xl">{s.hero_subheading}</p>
                    </Reveal>
                    <Reveal delay={360} className="md:col-span-6 flex flex-col sm:flex-row gap-3 md:justify-end">
                        <PillButton to="/projects" variant="light" testid="hero-cta-projects">Explore Projects</PillButton>
                        <PillButton to="/what-we-do" variant="ghost" testid="hero-cta-what-we-do">What We Do</PillButton>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function Intro() {
    const s = useSettings();
    return (
        <section data-testid="home-intro" className="bg-cream py-24 md:py-36">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
                <div className="lg:col-span-5">
                    <Reveal><Eyebrow>Our approach</Eyebrow></Reveal>
                    <Reveal delay={100}><h2 className="font-serif text-5xl sm:text-6xl text-forest leading-none tracking-tight mt-8">Built for <em className="italic">Tomorrow.</em></h2></Reveal>
                </div>
                <div className="lg:col-span-7 lg:pt-16">
                    <Reveal delay={150}><p className="text-xl md:text-2xl text-ink leading-relaxed font-light">{s.intro_text}</p></Reveal>
                    <Reveal delay={250} className="mt-14 grid sm:grid-cols-3 gap-px bg-hairline rounded-2xl overflow-hidden">
                        {[[ScanSearch, "We discover.", "AI-powered intelligence meets local knowledge to find overlooked opportunities."], [FileCheck2, "We unlock.", "We secure land, navigate planning and create development-ready projects."], [Handshake, "We invest.", "We put our capital to work alongside our partners, aligning interests."]].map(([Icon, t, b]) => (
                            <div key={t} className="bg-cream p-6 md:p-7" data-testid={`approach-${t.split(" ")[1].replace(".", "").toLowerCase()}`}>
                                <Icon className="h-6 w-6 text-moss mb-6" strokeWidth={1.5} />
                                <p className="font-serif text-2xl text-forest mb-3">{t}</p>
                                <p className="text-sm text-muted_text leading-relaxed">{b}</p>
                            </div>
                        ))}
                    </Reveal>
                    <Reveal delay={300} className="mt-10"><ArrowLink to="/land-intelligence" testid="home-land-intelligence-link">See how our Land Intelligence works</ArrowLink></Reveal>

                </div>
            </div>
        </section>
    );
}

function ActivityCard({ a, i }) {
    const s = useSettings();
    const Icon = a.icon;
    const inner = (
        <>
            <div className="flex items-center justify-between mb-6">
                <span className="mono-label text-moss">{a.n}</span>
                <Icon className="h-5 w-5 text-moss" strokeWidth={1.5} />
            </div>
            <h3 className="font-serif text-2xl md:text-3xl text-forest leading-tight mb-4">{a.title}</h3>
            <p className="text-sm text-muted_text leading-relaxed">{a.short}</p>
            <ArrowRight className="mt-auto pt-4 h-10 w-10 text-forest transition-transform duration-300 group-hover:translate-x-1" strokeWidth={1} />
        </>
    );
    const cls = "group flex flex-col bg-cream p-8 md:p-10 min-h-[200px] hover:bg-surface transition-colors";
    return (
        <Reveal delay={i * 80} className="h-full">
            {a.cta.external
                ? <OutLink href={s[a.cta.external]} event="verde_capital_click" label="home-activities" icon={false} testid={`activity-card-${a.id}`} className={`${cls} h-full !items-stretch`}>{inner}</OutLink>
                : <Link to={`/what-we-do#${a.id}`} data-testid={`activity-card-${a.id}`} className={`${cls} h-full`}>{inner}</Link>}
        </Reveal>
    );
}

function WhatWeDo() {
    const [core, ...others] = ACTIVITIES;
    return (
        <section data-testid="home-what-we-do" className="bg-cream pb-24 md:pb-36">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14 border-t border-hairline pt-16">
                    <div>
                        <Reveal><Eyebrow>What we do</Eyebrow></Reveal>
                        <Reveal delay={100}><h2 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.04] tracking-tight mt-8 max-w-2xl">Four activities. <em className="italic">One business.</em></h2></Reveal>
                    </div>
                    <ArrowLink to="/what-we-do" testid="home-what-we-do-link">How we work</ArrowLink>
                </div>
                <div className="grid lg:grid-cols-12 gap-px bg-hairline rounded-[1.75rem] overflow-hidden">
                    <Link to="/what-we-do#land-development" data-testid="activity-card-land-development"
                          className="group lg:col-span-6 lg:row-span-2 relative isolate overflow-hidden bg-forest text-cream p-8 md:p-12 min-h-[520px] flex flex-col img-zoom">
                        <img src="/v2050-sintra_render.jpg" alt="" aria-hidden="true" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-50" loading="lazy" />
                        <div className="absolute inset-0 -z-10 bg-gradient-to-t from-forest via-forest/70 to-forest/30" />
                        <div className="flex items-center justify-between">
                            <span className="mono-label text-sage">{core.n} · Core</span>
                            <core.icon className="h-5 w-5 text-sage" strokeWidth={1.5} />
                        </div>
                        <div className="mt-auto">
                            <h3 className="font-serif text-4xl md:text-5xl leading-[1.04] mb-6 max-w-md">{core.title}</h3>
                            <p className="text-base text-cream/80 leading-relaxed max-w-md mb-8">{core.short}</p>
                            <span className="inline-flex items-center gap-3 mono-label text-cream group-hover:text-sage transition-colors">Our model <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></span>
                        </div>
                    </Link>
                    <div className="lg:col-span-6 grid gap-px bg-hairline">
                        {others.map((a, i) => <ActivityCard key={a.id} a={a} i={i} />)}
                    </div>
                </div>
            </div>
        </section>
    );
}

function FeaturedProject() {
    const projects = useProjects();
    const p = projects?.find((x) => x.featured) || projects?.[0];
    if (!p) return projects === null ? <div className="h-[60vh] bg-surface" /> : null;
    return (
        <section data-testid="home-featured-project" className="bg-surface py-24 md:py-36">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <Reveal><Eyebrow>Featured project</Eyebrow></Reveal>
                <Reveal delay={100} className="mt-10 relative overflow-hidden rounded-[2rem] img-zoom">
                    <Link to={`/projects/${p.slug}`} className="block aspect-[4/5] sm:aspect-[16/9]" data-testid="featured-project-image-link">
                        <img src={p.hero_image} alt={`${p.title}, ${p.location}`} className="h-full w-full object-cover" loading="lazy" />
                    </Link>
                    <div className="absolute left-5 top-5 flex flex-wrap gap-2 pointer-events-none">
                        <StatusBadge status={p.status} />
                    </div>
                </Reveal>
                <div className="mt-12 grid lg:grid-cols-12 gap-10">
                    <Reveal className="lg:col-span-7">
                        <p className="mono-label text-moss mb-4">{p.location} · {p.type} · {p.program}</p>
                        <h2 data-testid="featured-project-headline" className="font-serif text-4xl sm:text-5xl text-forest leading-[1.04] tracking-tight">{p.headline || p.title}</h2>
                    </Reveal>
                    <Reveal delay={150} className="lg:col-span-5 lg:pt-10">
                        <p className="text-base md:text-lg text-muted_text leading-relaxed">{p.summary}</p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <PillButton to={`/projects/${p.slug}#request-information`} testid="featured-project-cta">Request Project Information</PillButton>
                            <PillButton to="/projects" variant="outline" testid="featured-project-all">All projects</PillButton>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
}

function InvestAlongside() {
    return (
        <section data-testid="home-invest-alongside" className="bg-cream py-24 md:py-36">
            <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 items-center">
                <div className="lg:col-span-5 lg:order-2">
                    <ol className="border-t border-forest/15" data-testid="invest-steps">
                        {INVEST_STEPS.map(([t, d], i) => (
                            <Reveal as="li" key={t} delay={i * 100} className="grid grid-cols-[3rem_1fr] gap-4 py-7 border-b border-forest/15">
                                <span className="mono-label text-moss pt-1">0{i + 1}</span>
                                <div>
                                    <p className="font-serif text-2xl text-forest leading-tight">{t}</p>
                                    <p className="mt-2 text-sm md:text-base text-muted_text leading-relaxed">{d}</p>
                                </div>
                            </Reveal>
                        ))}
                    </ol>
                </div>
                <div className="lg:col-span-7 lg:order-1">
                    <Reveal><Eyebrow>Invest with Us</Eyebrow></Reveal>
                    <Reveal delay={100}><h2 className="font-serif text-4xl sm:text-5xl text-forest leading-[1.04] tracking-tight mt-8">Land investment, <em className="italic">made accessible.</em></h2></Reveal>
                    <Reveal delay={200} className="mt-8 space-y-5 text-base md:text-lg text-muted_text leading-relaxed max-w-xl">
                        <p>The best land in Portugal rarely reaches the open market — and when it does, it takes local knowledge, planning expertise and time to judge what it is really worth.</p>
                        <p>We open that market to investors. We source the opportunities, do the groundwork and structure each project, so selected investors can participate in qualifying land and development projects across Portugal.</p>
                    </Reveal>
                    <p className="mt-6 text-sm text-moss max-w-xl" data-testid="own-equity-note">Alongside our investors, we typically commit 5–10% of the project equity ourselves.</p>
                    <Reveal delay={300} className="mt-10"><PillButton to="/investor-circle" testid="invest-cta">Join the Investor Circle</PillButton></Reveal>
                </div>
            </div>
        </section>
    );
}

const INVEST_STEPS = [
    ["Sourced", "Off-market land found through our technology and local network."],
    ["Studied", "Planning, legal and market groundwork done before anything is offered."],
    ["Structured", "Each opportunity presented with clear terms and documentation."],
]

function News() {
    return (
        <section data-testid="home-news" className="bg-forest py-24 md:py-36">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-14">
                    <div>
                        <Reveal><Eyebrow dark>Latest news · Get The Plot</Eyebrow></Reveal>
                        <Reveal delay={100}><h2 className="font-serif text-4xl sm:text-5xl text-cream leading-[1.04] tracking-tight mt-8">Daily <em className="italic text-sage">real estate news.</em></h2></Reveal>
                    </div>
                    <ArrowLink to="/news" dark testid="home-news-more">Read More News</ArrowLink>
                </div>
                <GetThePlotFeed />
                <p className="mt-10 flex flex-wrap items-center gap-x-3 gap-y-2 text-sm text-cream/60" data-testid="home-news-ai-note">
                    <Cpu className="h-4 w-4 text-sage" strokeWidth={1.5} />
                    Curated and written automatically by our Land Intelligence AI framework.
                    <Link to="/land-intelligence" className="text-sage underline underline-offset-4 hover:text-cream transition-colors" data-testid="home-news-ai-link">See how it works</Link>
                </p>
            </div>
        </section>
    );
}

export default function Verde2050() {
    return (
        <div data-testid="verde2050-page" className="bg-cream text-ink min-h-screen">
            <SEO path="/" og="home" title="Verde 2050 — Built for Tomorrow | Land development & investment in Portugal"
                 description="Verde 2050 is an independent, founder-led real estate development and investment company in Greater Lisbon. We discover exceptional land, unlock development potential and invest alongside partners." />
            <GroupHeader />
            <Hero />
            <Intro />
            <WhatWeDo />
            <FeaturedProject />
            <WalkTheLand />
            <InvestAlongside />
            <News />
            <ClosingCTA testid="home-contact" />
            <GroupFooter />
        </div>
    );
}
